import type { ApiErrorDto } from '@eskoolhub/types';

const BASE = import.meta.env.VITE_API_BASE ?? '';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/**
 * The access token lives in memory only — never localStorage, so an XSS bug
 * cannot walk off with a long-lived credential. Session continuity comes from
 * the httpOnly refresh cookie instead.
 */
let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Internal: prevents an infinite refresh loop. */
  skipRefresh?: boolean;
}

async function rawRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, skipRefresh, headers, ...rest } = options;

  const response = await fetch(`${BASE}${path}`, {
    ...rest,
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = (payload as ApiErrorDto | null)?.error;

    // One silent refresh attempt, then hand control back to the auth layer.
    if (response.status === 401 && !skipRefresh && error?.code !== 'INVALID_CREDENTIALS') {
      const refreshed = await tryRefresh();
      if (refreshed) return rawRequest<T>(path, { ...options, skipRefresh: true });
      onUnauthenticated?.();
    }

    throw new ApiRequestError(
      response.status,
      error?.code ?? 'REQUEST_FAILED',
      error?.message ?? 'Something went wrong',
      error?.fields,
    );
  }

  return payload as T;
}

async function tryRefresh(): Promise<boolean> {
  try {
    const response = await fetch(`${BASE}/api/auth/refresh`, { method: 'POST', credentials: 'include' });
    if (!response.ok) return false;
    const data = (await response.json()) as { accessToken: string };
    accessToken = data.accessToken;
    return true;
  } catch {
    return false;
  }
}

export const api = {
  get: <T>(path: string) => rawRequest<T>(path),
  post: <T>(path: string, body?: unknown) => rawRequest<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => rawRequest<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => rawRequest<T>(path, { method: 'DELETE' }),
  refresh: tryRefresh,
};
